import React, { useEffect, useState } from 'react';
import { EmployeeRole } from '@fycom/shared-types';
import type { SeedEmployee as Employee, InactiveReason, Jornada } from '../data/types';
import { ESTADO_LABELS, JORNADA_LABELS } from '../data/types';
import { fetchEmployees, updateEmployee } from '../lib/api';

interface EmployeesListProps {
  defaultAreas: string[];
  companyAreas: Record<'Fischer' | 'Unitax', string[]>;
  resetCounter: number;
}

const COMPANY_OPTIONS = ['Fischer', 'Unitax', ''] as const;
const ROLE_OPTIONS: EmployeeRole[] = ['Empleado', 'Externo', 'Procurador', 'Practicante'];

// Color del badge de Estado:
//   Activo → verde · Inactivo (desvinculado) → rojo · pausas temporales → azul.
function estadoBadgeClass(isActive: boolean, reason?: InactiveReason): string {
  if (isActive) return 'bg-emerald-100 text-emerald-800';
  if (reason === 'pausado_estudios' || reason === 'licencia_maternal') {
    return 'bg-sky-100 text-sky-800';
  }
  return 'bg-rose-100 text-rose-800'; // inactivo / sin tag
}

export function EmployeesList({ defaultAreas, companyAreas, resetCounter }: EmployeesListProps) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [initialEmployees, setInitialEmployees] = useState<Employee[]>([]);
  const [draftValues, setDraftValues] = useState<Record<string, Partial<Employee>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resetCounter > 0) {
      // El store ya fue reseteado al seed; recargamos desde la fuente.
      setDraftValues({});
      setEditingId(null);
      setError(null);
      loadEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetCounter]);

  const loadEmployees = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchEmployees();
      setEmployees(data);
      if (initialEmployees.length === 0) {
        setInitialEmployees(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setIsLoading(false);
    }
  };

  const getAreaOptions = (company: string) => {
    if (company === 'Fischer' || company === 'Unitax') {
      return companyAreas[company] ?? defaultAreas;
    }
    return defaultAreas;
  };

  const applyDraftUpdate = (id: string, updates: Partial<Employee>) => {
    setDraftValues((current) => ({
      ...current,
      [id]: { ...current[id], ...updates },
    }));

    setEmployees((current) =>
      current.map((emp) => (emp.id === id ? { ...emp, ...updates } : emp))
    );
  };

  const saveEmployee = async (id: string) => {
    const draft = draftValues[id];
    if (!draft) {
      setEditingId(null);
      return;
    }

    const changes = {
      rut: draft.rut,
      company: draft.company,
      area: draft.area,
      role: draft.role,
      targetBillablePct: draft.targetBillablePct,
      isActive: draft.isActive,
      inactiveReason: draft.inactiveReason,
      jornada: draft.jornada,
    };

    try {
      const updated = await updateEmployee(id, changes);
      setEmployees((current) => current.map((emp) => (emp.id === id ? updated : emp)));
      setDraftValues((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      setEditingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar colaborador');
    }
  };

  const startEditing = (id: string) => {
    setEditingId(id);
    const employee = employees.find((emp) => emp.id === id);
    if (employee) {
      setDraftValues((current) => ({ ...current, [id]: { ...employee } }));
    }
  };

  const filteredEmployees = employees.filter((e) =>
    e.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-slate-600">Cargando colaboradores...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <input
          type="text"
          placeholder="Buscar colaborador..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fycom-navy"
        />
        <button
          onClick={loadEmployees}
          className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-sm transition"
        >
          Actualizar
        </button>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-200">
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Nombre</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Empresa</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Área</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Rol</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Estado</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">Jornada</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-700">RUT</th>
              <th className="px-4 py-3 text-center font-semibold text-slate-700">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((emp) => {
              const isEditing = editingId === emp.id;
              const draft = draftValues[emp.id] ?? emp;
              const areaOptions = getAreaOptions(draft.company || emp.company);

              return (
                <tr
                  key={emp.id}
                  className="border-b border-slate-200 hover:bg-slate-50 transition"
                >
                  <td className="px-4 py-3 text-slate-900">{emp.displayName}</td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <select
                        value={draft.company}
                        onChange={(e) => applyDraftUpdate(emp.id, { company: e.target.value })}
                        className="px-2 py-1 border border-slate-300 rounded text-sm"
                        autoFocus
                      >
                        {COMPANY_OPTIONS.map((company) => (
                          <option key={company || 'empty'} value={company}>
                            {company || '(Sin asignar)'}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-slate-600">{emp.company || '—'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <select
                        value={draft.area ?? ''}
                        onChange={(e) => {
                          const areaValue = e.target.value || null;
                          applyDraftUpdate(emp.id, { area: areaValue as Employee['area'] });
                        }}
                        className="px-2 py-1 border border-slate-300 rounded text-sm"
                      >
                        <option value="">—</option>
                        {areaOptions.map((area) => (
                          <option key={area} value={area}>
                            {area}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="inline-block px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                        {emp.area || '—'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <select
                        value={draft.role ?? 'Empleado'}
                        onChange={(e) => applyDraftUpdate(emp.id, { role: e.target.value as EmployeeRole })}
                        className="px-2 py-1 border border-slate-300 rounded text-sm"
                      >
                        {ROLE_OPTIONS.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="inline-block px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                        {emp.role || '—'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <select
                        value={draft.isActive ? 'activo' : draft.inactiveReason ?? 'inactivo'}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === 'activo') {
                            applyDraftUpdate(emp.id, { isActive: true, inactiveReason: undefined });
                          } else {
                            applyDraftUpdate(emp.id, { isActive: false, inactiveReason: v as InactiveReason });
                          }
                        }}
                        className="px-2 py-1 border border-slate-300 rounded text-sm"
                      >
                        <option value="activo">Activo</option>
                        <option value="inactivo">Inactivo</option>
                        <option value="pausado_estudios">Pausado por Estudios</option>
                        <option value="licencia_maternal">Licencia Maternal</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${estadoBadgeClass(
                          emp.isActive,
                          emp.inactiveReason
                        )}`}
                      >
                        {emp.isActive
                          ? 'Activo'
                          : ESTADO_LABELS[emp.inactiveReason ?? 'inactivo']}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <select
                        value={draft.jornada ?? 'full_time'}
                        onChange={(e) =>
                          applyDraftUpdate(emp.id, { jornada: e.target.value as Jornada })
                        }
                        className="px-2 py-1 border border-slate-300 rounded text-sm"
                      >
                        <option value="full_time">Full-Time</option>
                        <option value="part_time">Part-Time</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                          (emp.jornada ?? 'full_time') === 'part_time'
                            ? 'bg-violet-100 text-violet-800'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {JORNADA_LABELS[emp.jornada ?? 'full_time']}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {isEditing ? (
                      <input
                        type="text"
                        value={draft.rut}
                        onChange={(e) => applyDraftUpdate(emp.id, { rut: e.target.value })}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
                      />
                    ) : (
                      emp.rut || '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => {
                          if (isEditing) {
                            saveEmployee(emp.id);
                          } else {
                            startEditing(emp.id);
                          }
                        }}
                        className="px-3 py-1 text-sm bg-fycom-navy text-white rounded hover:bg-blue-900 transition"
                      >
                        {isEditing ? 'Guardar' : 'Editar'}
                      </button>
                      {isEditing && (
                        <button
                          onClick={() => {
                            setDraftValues((current) => {
                              const copy = { ...current };
                              delete copy[emp.id];
                              return copy;
                            });
                            setEmployees((current) => current.map((item) => (item.id === emp.id ? emp : item)));
                            setEditingId(null);
                          }}
                          className="px-3 py-1 text-sm bg-slate-200 text-slate-700 rounded hover:bg-slate-300 transition"
                        >
                          Cancelar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredEmployees.length === 0 && (
        <div className="text-center py-8 text-slate-600">
          No se encontraron colaboradores
        </div>
      )}

      <div className="text-xs text-slate-600">
        Total: {filteredEmployees.length} de {employees.length} colaboradores
      </div>
    </div>
  );
}
